<script setup lang="ts">
import {ref, onMounted, watch, computed} from 'vue'
import { Icon } from '@iconify/vue'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
// @ts-ignore
import html2pdf from 'html2pdf.js'
import { getPackingListById } from "@/api/packingList"
import { getBatchById } from "@/api/batches"
import { getOrderById } from "@/api/orders"
import { getRollById } from "@/api/rolls"
import { formatedDate } from "@/constants"
import type {Customer} from "@/types/customer-types";
import {getCustomerList} from "@/api/customers";
import {getSupplierList} from "@/api/suppliers";
import type {Supplier} from "@/types/supplier-types";

const props = defineProps<{
  visible: boolean,
  onClose: () => void,
  packingListId: string,
}>()

const packingListData = ref<any>(null)
const stockList = ref<any[]>([])
const orderData = ref<any>(null)
const batchData = ref<any>(null)
const customerList = ref<Customer[] | undefined>()
const supplierList = ref<Supplier[] | undefined>()

const fetchPackingListData = async () => {
  try {
    packingListData.value = await getPackingListById(props.packingListId)

    if (packingListData.value?.orderId) {
      orderData.value = await getOrderById(packingListData.value.orderId)
    }

    if (packingListData.value?.batchId) {
      batchData.value = await getBatchById(packingListData.value.batchId)
    }

    if (packingListData.value?.rollIds && packingListData.value.rollIds.length > 0) {
      const rollPromises = packingListData.value.rollIds.map(async (id: string) => {
        const roll = await getRollById(id)
        if (roll.batchId) {
          const batch = await getBatchById(roll.batchId)
          return {
            ...roll,
            batchNumber: batch.batchNumber
          }
        }
        return roll
      })
      stockList.value = await Promise.all(rollPromises)
    }
  } catch (error) {
    console.error('Error fetching packing list data:', error)
  }
}

const formatDate = (date: string) => {
  return formatedDate(date)
}

const getPackingListNumber = () => {
  return packingListData.value?.packingListId
}

const isSupplierPackingList = computed(() => {
  return packingListData.value?.packingListType === 0;
});

const currentCustomer = computed(() => {
  if (isSupplierPackingList.value) return null;
  if (!packingListData.value?.customerId || !customerList.value) return null;
  return customerList.value.find((customer) => customer.id === packingListData.value?.customerId) || null;
});

const currentSupplier = computed(() => {
  if (!isSupplierPackingList.value) return null;
  if (!packingListData.value?.finishingSupplierId || !supplierList.value) return null;
  return supplierList.value.find((supplier) => supplier.id === packingListData.value?.finishingSupplierId) || null;
});

const deliveryAddress = computed(() => {
  if (isSupplierPackingList.value) {
    if (!currentSupplier.value || !currentSupplier.value.addresses || currentSupplier.value.addresses.length === 0) {
      return null;
    }
    return currentSupplier.value.addresses.find((address) => address.addressType === 1) || currentSupplier.value.addresses[0];
  } else {
    if (!currentCustomer.value || !currentCustomer.value.addresses || currentCustomer.value.addresses.length === 0) {
      return null;
    }
    return currentCustomer.value.addresses.find((address) => address.addressType === 1) || currentCustomer.value.addresses[0];
  }
});

const businessName = computed(() => {
  if (isSupplierPackingList.value) {
    return currentSupplier.value?.name || 'Supplier information not available';
  } else {
    return currentCustomer.value?.businessName || 'Customer information not available';
  }
});

const calculateTotalWeight = computed(() => {
  if (!stockList.value || stockList.value.length === 0) return 0;
  const totalWeight = stockList.value.reduce((sum, roll) => sum + (parseFloat(roll.weight) || 0), 0);
  return totalWeight.toFixed(2);
});

const calculateTotalLength = computed(() => {
  if (!stockList.value || stockList.value.length === 0) return 0;
  const totalLength = stockList.value.reduce((sum, roll) => sum + (parseFloat(roll.lengthValue) || 0), 0);
  return totalLength.toFixed(2);
});

const onDownloadPackingList = () => {
  const packingListElement = document.getElementById('packing-list-pdf')
  if (packingListElement) {
    html2pdf()
        .set({
          margin: 5,
          filename: `${getPackingListNumber()}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .from(packingListElement)
        .save()
  }
}

watch(() => props.visible, (newValue) => {
  if (newValue) {
    fetchPackingListData()
  }
})

onMounted(async () => {
  if (props.visible) {
    await fetchPackingListData()
  }
  customerList.value = await getCustomerList()
  supplierList.value = await getSupplierList()
})
</script>

<template>
  <Dialog :visible="visible" modal :draggable="false" :style="{ width: '70rem' }" :closable="false">
    <div class="flex justify-between items-center mb-4">
      <Button
          link
          class="flex items-center gap-x-2 group hover:text-primary-default"
          @click="onClose"
      >
        <Icon icon="material-symbols:arrow-back-rounded" class="text-3xl group-hover:translate-x-2 transition-transform" />
        <span>Back to Packing List</span>
      </Button>
      <div class="flex gap-4">
        <Button label="Download as PDF" class="p-button-danger" @click="onDownloadPackingList">
          <template #icon>
            <Icon icon="material-symbols:download-2" class="text-xl ml-3 order-1" />
          </template>
        </Button>
      </div>
    </div>

    <h1 class="font-semibold text-xl mb-2">Packing List</h1>
    <hr class="mb-4" />

    <div class="p-6 bg-white rounded-lg shadow" id="packing-list-pdf">
      <div class="flex justify-between items-center mb-4">
        <div>
          <img src="@/assets/images/logo.png" alt="Company Logo" class="h-10" />
        </div>
        <p class="text-primary-500 text-xl">{{ packingListData?.packingListId }}</p>

        <div>
          <p class="text-gray-600 text-sm">Unit 8 - 9, Lime Kilns Business Park<br />Lime Kilns Way, Hinckley, LE10 3EL</p>
          <p class="text-sm">Tel: +44 (0)1455 254350<br />Fax: +44 (0)1455 238998</p>
        </div>
      </div>

      <div class="mt-6 flex flex-row justify-between gap-4 p-4 bg-gray-50 rounded">
        <div>
          <h4 class="font-semibold">Packing List Details</h4>
          <p class="text-sm">Packing List Number: {{ packingListData?.packingListId }}</p>
          <p class="text-sm" v-if="orderData">Order Number: {{ orderData.orderNumber }}</p>
          <p class="text-sm">Created Date: {{ packingListData?.packingListDate ? formatDate(packingListData.packingListDate) : '-' }}</p>
        </div>
        <div>
          <h4 class="font-semibold">{{ isSupplierPackingList ? 'Supplier Address' : 'Delivery Address' }}</h4>
          <p class="text-sm font-medium">{{ isSupplierPackingList ? 'Supplier' : 'Customer' }} Name: {{ businessName }}</p>
          <template v-if="deliveryAddress">
            <p class="text-sm" v-if="deliveryAddress.line1">
              {{ deliveryAddress.line1 }}
              <template v-if="deliveryAddress.line2">,<br />{{ deliveryAddress.line2 }}</template>
            </p>
            <p class="text-sm">
              <template v-if="deliveryAddress.city">{{ deliveryAddress.city }}</template>
              <template v-if="deliveryAddress.city && deliveryAddress.postCode">, </template>
              <template v-if="deliveryAddress.postCode">{{ deliveryAddress.postCode }}</template>
              <template v-if="(deliveryAddress.city || deliveryAddress.postCode) && deliveryAddress.country">, </template>
              <template v-if="deliveryAddress.country">{{ deliveryAddress.country }}</template>
            </p>
            <p class="text-sm" v-if="deliveryAddress.region">Region: {{ deliveryAddress.region }}</p>
          </template>
          <p class="text-sm" v-else>{{ isSupplierPackingList ? 'Supplier' : 'Customer' }} information not available</p>
        </div>
      </div>

      <div class="mt-3 overflow-x-auto">
        <h4 class="font-semibold mb-1 text-xs">Stock Items</h4>
        <table class="min-w-full text-left border-0">
          <thead class="bg-gray-50">
          <tr>
            <th class="px-2 py-1 text-xs">Roll Number</th>
            <th class="px-2 py-1 text-xs">Weight</th>
            <th class="px-2 py-1 text-xs" v-if="!isSupplierPackingList">Length</th>
            <th class="px-2 py-1 text-xs">Batch Number</th>
            <th class="px-2 py-1 text-xs">Date Knitted</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="roll in stockList" :key="roll.id" class="border-0">
            <td class="px-2 py-1 text-xs">{{ roll.rollNumber }}</td>
            <td class="px-2 py-1 text-xs">{{ roll.weight }}</td>
            <td class="px-2 py-1 text-xs" v-if="!isSupplierPackingList">{{ roll.lengthValue }}</td>
            <td class="px-2 py-1 text-xs">{{ roll.batchNumber }}</td>
            <td class="px-2 py-1 text-xs">{{ formatDate(roll.dateKnitted) }}</td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="mt-6 p-4 bg-gray-50 rounded">
        <div class="flex justify-between">
          <div class="w-1/3 pr-2">
            <h4 class="font-semibold mb-2 text-sm">Signature</h4>
            <div class="border border-gray-300 rounded p-2 h-16">
            </div>
          </div>
         <div class="w-1/3 px-2">
           <h4 class="font-semibold mb-2 text-sm">Summary</h4>
           <div class="border border-gray-300 rounded p-2 h-16">
             <p class="text-xs mb-1">Total Rolls: {{ stockList.length }}</p>
             <p class="text-xs">Total Weight: {{ calculateTotalWeight }} kg</p>
             <p class="text-xs" v-if="!isSupplierPackingList">Total Length: {{ calculateTotalLength }} m</p>
          </div>
         </div>
          <div class="w-1/3 pl-2">
            <h4 class="font-semibold mb-2 text-sm">Notes</h4>
            <div class="border border-gray-300 rounded p-2 h-16 overflow-auto">
              <p class="text-xs">{{ packingListData?.notes || 'No additional notes provided.' }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-9 p-4 bg-gray-50 rounded">
        <p class="font-bold text-center">Claim for non-delivery, shortages and pilferage</p>
        <p class="text-sm">Please note that we accept no responsibility for claims covered by this packing
          list unless we and the carrier are notified in writing within three days
          for non-delivery and immediately for shortage and pilferage</p>
      </div>
    </div>
  </Dialog>
</template>